import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button, Input, Modal, Select } from '../../components/ui';
import { formatMoney } from '../../lib/format';
import { useCreateHourBlock } from './api';

/** Only what the form needs — every support client is a valid target, bank or not. */
export interface TopUpTarget {
  clientId: string;
  clientName: string;
  defaultRate: number | null;
}

/**
 * Top up a client's bank.
 *
 * Three fields, and two of them are prefilled: client (from the row you clicked)
 * and rate (what this client last bought at). Expiry and note are optional and
 * live behind nothing — they are simply blank. Everything else the model needs
 * — amount, status, ledger — is derived, because every required field is a step.
 */
export function AddHoursModal({
  open,
  onOpenChange,
  clients,
  initialClientId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clients: TopUpTarget[];
  initialClientId?: string | null;
}) {
  const [clientId, setClientId] = useState('');
  const [hours, setHours] = useState('');
  const [rate, setRate] = useState('');
  const [expiresAt, setExpiresAt] = useState('');
  const [note, setNote] = useState('');
  const [unpaid, setUnpaid] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCreateHourBlock();

  const selected = useMemo(
    () => clients.find((c) => c.clientId === clientId) ?? null,
    [clients, clientId],
  );

  // Opening from a row prefills the client; the rate follows it, so a top-up is
  // "type the hours, press Add".
  useEffect(() => {
    if (!open) return;
    const first = initialClientId ?? clients[0]?.clientId ?? '';
    setClientId(first);
    setHours('');
    setExpiresAt('');
    setNote('');
    setUnpaid(false);
    setError(null);
  }, [open, initialClientId, clients]);

  useEffect(() => {
    if (!open) return;
    const next = clients.find((c) => c.clientId === clientId);
    setRate(next?.defaultRate != null ? String(next.defaultRate) : '');
  }, [open, clientId, clients]);

  const hoursNum = Number(hours);
  const rateNum = Number(rate);
  const amount =
    Number.isFinite(hoursNum) && Number.isFinite(rateNum) && hours && rate ? hoursNum * rateNum : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!clientId) {
      setError('Pick a client.');
      return;
    }
    if (!hours || !Number.isFinite(hoursNum) || hoursNum <= 0) {
      setError('Enter a positive number of hours.');
      return;
    }
    if (!rate || !Number.isFinite(rateNum) || rateNum < 0) {
      setError('Enter the rate these hours were bought at.');
      return;
    }
    try {
      await create.mutateAsync({
        clientId,
        hours: hoursNum,
        rate: rateNum,
        expiresAt: expiresAt || null,
        note: note.trim() || null,
        source: 'MANUAL',
        ...(unpaid ? { paid: false } : {}),
      } as never);
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not record the purchase.');
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Add hours"
      description="Hours are recorded at the rate agreed today; using them never re-prices them."
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="add-hours-form" loading={create.isPending}>
            Add hours
          </Button>
        </>
      }
    >
      <form id="add-hours-form" onSubmit={submit} className="space-y-4">
        <Select
          label="Client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          options={clients.map((c) => ({ value: c.clientId, label: c.clientName }))}
          placeholder={clients.length ? undefined : 'No support clients'}
        />
        <div className="grid grid-cols-2 gap-3">
          <Input
            label="Hours"
            type="number"
            min="0.25"
            step="0.25"
            inputMode="decimal"
            value={hours}
            onChange={(e) => setHours(e.target.value)}
            placeholder="40"
            autoFocus
          />
          <Input
            label="Rate"
            type="number"
            min="0"
            step="1"
            inputMode="decimal"
            value={rate}
            onChange={(e) => setRate(e.target.value)}
            placeholder="150"
            hint={selected?.defaultRate != null ? 'Their last purchase rate' : 'No previous purchase'}
          />
        </div>
        <Input
          label="Expires (optional)"
          type="date"
          value={expiresAt}
          onChange={(e) => setExpiresAt(e.target.value)}
          hint="Blank = the hours never expire. Expiring blocks are always spent first."
        />
        <Input
          label="Note (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Renewal block"
        />
        <label className="flex items-start gap-2 text-body-sm text-grey-600">
          <input
            type="checkbox"
            checked={unpaid}
            onChange={(e) => setUnpaid(e.target.checked)}
            className="mt-0.5 h-4 w-4 rounded border-grey-300 text-accent focus:ring-accent/30"
          />
          <span>
            Not paid yet — hold the hours
            <span className="block text-caption text-grey-500">
              A block only grants hours once the money has arrived.
            </span>
          </span>
        </label>
        <div className="flex items-center justify-between rounded-lg bg-grey-50 px-3 py-2 text-body-sm">
          <span className="text-grey-500">Purchase value</span>
          <span className="num font-semibold text-secondary">
            {amount === null ? '—' : formatMoney(amount)}
          </span>
        </div>
        {error && <p className="text-body-sm text-error">{error}</p>}
      </form>
    </Modal>
  );
}
